// features/workstations/dashboard-sections/thesis-stress-test.js
// ═══════════════════════════════════════════════════════════
// THESIS STRESS TEST — live health of every active thesis
// ═══════════════════════════════════════════════════════════
// Tier A / Jarvis Surface. Sibling of the Theses widget, but
// with a different job:
//
//   Theses widget         = MANAGE
//                           (state, compose, pause, retire,
//                            infer candidates)
//
//   Thesis Stress Test    = MONITOR
//                           (at-a-glance pressure gauge per
//                            thesis, tension count, drift
//                            drivers, expand for full drift)
//
// Read-only. Reuses the existing engine APIs — no new
// endpoints, no new migrations. When a thesis drifts into
// \"weakening\" or \"contradicted\", this widget surfaces it
// without the user having to open the Theses widget.
//
// If zero theses: directs user to the Theses widget to state
// one. The stress test is useless without theses to stress.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PractitionerLens,
  MetaField,
} from '../../../components/widgets/primitives';

const STATE_TONES = {
  strengthening: 'sage',
  holding:       'steel',
  active:        'gold',
  weakening:     'amber',
  contradicted:  'terra',
  paused:        'steel',
};

const STATE_LABELS = {
  strengthening: 'strengthening',
  holding:       'holding',
  active:        'active',
  weakening:     'weakening',
  contradicted:  'contradicted',
  paused:        'paused',
};

const SEVERITY_TONES = {
  critical: 'terra',
  high:     'terra',
  medium:   'amber',
  low:      'steel',
};

// Health score from the state + evidence ratio.
// 100 = strong support, 0 = fully contradicted. Used to sort
// theses so the riskiest surface first.
function healthScore(thesis) {
  const state = thesis.state;
  const evFor = (Array.isArray(thesis.evidence_for) ? thesis.evidence_for.length : 0);
  const evAgainst = (Array.isArray(thesis.evidence_against) ? thesis.evidence_against.length : 0);
  const evRatio = evFor + evAgainst > 0 ? evFor / (evFor + evAgainst) : 0.5;
  const stateBase = state === 'contradicted' ? 5
    : state === 'weakening'     ? 25
    : state === 'holding'        ? 55
    : state === 'strengthening'  ? 80
    : state === 'active'         ? 65
    : 50;
  // Mix 60% state + 40% evidence ratio
  return Math.round(stateBase * 0.6 + (evRatio * 100) * 0.4);
}

// ═══════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════
export default function ThesisStressTestSection({ workstation, userId }) {
  const [theses, setTheses] = useState([]);
  const [tensions, setTensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const domain = workstation?.domain_key || 'trading';

  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const [tRes, xRes] = await Promise.all([
        fetch(`/api/engines/theses?${new URLSearchParams({
          user_id: userId,
          domain,
          workstation_id: workstation.id,
        })}`, { cache: 'no-store' }),
        fetch(`/api/engines/tensions?${new URLSearchParams({
          user_id: userId,
          domain,
          workstation_id: workstation.id,
        })}`, { cache: 'no-store' }),
      ]);
      const tData = tRes.ok ? await tRes.json() : { theses: [] };
      const xData = xRes.ok ? await xRes.json() : { tensions: [] };
      setTheses(Array.isArray(tData.theses) ? tData.theses : []);
      setTensions(Array.isArray(xData.tensions) ? xData.tensions : []);
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId, domain]);

  useEffect(() => { load(); }, [load]);

  // Index tensions by thesis_id
  const tensionsByThesis = {};
  for (const t of tensions) {
    if (!t.thesis_id) continue;
    if (!tensionsByThesis[t.thesis_id]) tensionsByThesis[t.thesis_id] = [];
    tensionsByThesis[t.thesis_id].push(t);
  }
  // Sort each thesis’s tensions by severity desc
  const SEVERITY_ORDER = { critical: 4, high: 3, medium: 2, low: 1 };
  for (const id in tensionsByThesis) {
    tensionsByThesis[id].sort((a, b) => (SEVERITY_ORDER[b.severity] || 0) - (SEVERITY_ORDER[a.severity] || 0));
  }

  // Sort theses: lowest health first (riskiest up top)
  const sorted = [...theses]
    .filter(t => t.state !== 'retired' && t.state !== 'paused')
    .map(t => ({ ...t, _health: healthScore(t) }))
    .sort((a, b) => a._health - b._health);

  // Aggregate counts for the eyebrow
  const counts = {
    total: sorted.length,
    contradicted: sorted.filter(t => t.state === 'contradicted').length,
    weakening: sorted.filter(t => t.state === 'weakening').length,
  };

  // ─── Render ───
  return (
    <section className="ws-stress">
      {loading && <div className="ws-stress-loading">Loading thesis health…</div>}

      {!loading && sorted.length === 0 && (
        <EmptyState error={error} />
      )}

      {!loading && sorted.length > 0 && (
        <>
          <Eyebrow parts={[
            'thesis health',
            `${counts.total} active`,
            counts.contradicted > 0 ? `${counts.contradicted} contradicted` : null,
            counts.weakening > 0 ? `${counts.weakening} weakening` : null,
          ]} />

          <SerifHeadline>{headlineFor(counts)}</SerifHeadline>
          <SerifSummary>{summaryFor(counts)}</SerifSummary>

          <div className="ws-stress-list">
            {sorted.map(t => (
              <ThesisHealthCard
                key={t.id}
                thesis={t}
                tensions={tensionsByThesis[t.id] || []}
              />
            ))}
          </div>

          <PractitionerLens label="how to read this">
            Riskiest theses surface at the top. Tensions are the
            engine’s automatic drift detection. Click any card for the
            full drift history. Resolve or dismiss tensions on the
            Today’s Briefing widget — that’s where the action bars live.
          </PractitionerLens>
        </>
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// EMPTY STATE
// ═══════════════════════════════════════════════════════════
function EmptyState({ error }) {
  return (
    <div className="ws-stress-empty">
      <Eyebrow>no active theses</Eyebrow>
      <SerifHeadline>Nothing to stress-test yet.</SerifHeadline>
      <SerifSummary>
        Stress test shows how your active theses are holding up against
        new evidence. State one in the Theses widget — the engine
        starts watching it immediately.
      </SerifSummary>
      {error && <div className="ws-stress-error">{error}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CARD
// ═══════════════════════════════════════════════════════════
function ThesisHealthCard({ thesis, tensions }) {
  const [expanded, setExpanded] = useState(false);
  const tone = STATE_TONES[thesis.state] || 'steel';
  const stateLabel = STATE_LABELS[thesis.state] || thesis.state;

  const evFor = Array.isArray(thesis.evidence_for) ? thesis.evidence_for.length : 0;
  const evAgainst = Array.isArray(thesis.evidence_against) ? thesis.evidence_against.length : 0;
  const totalEv = evFor + evAgainst;
  const pctFor = totalEv > 0 ? (evFor / totalEv) * 100 : 50;
  const confPct = typeof thesis.confidence === 'number'
    ? Math.round(thesis.confidence * 100)
    : null;

  const topTension = tensions[0] || null;
  const openTensionCount = tensions.length;

  return (
    <div className={`ws-stress-card ws-stress-card-${tone}`}>
      <button
        type="button"
        className="ws-stress-card-head"
        onClick={() => setExpanded(v => !v)}
        title={expanded ? 'Collapse' : 'Expand'}
      >
        <div className="ws-stress-card-head-top">
          <span className={`ws-stress-state ws-stress-state-${tone}`}>{stateLabel}</span>
          <div className="ws-stress-card-meta">
            {confPct != null && (
              <span className="ws-stress-confidence">{confPct}%</span>
            )}
            {openTensionCount > 0 && (
              <span className={`ws-stress-tension-count ws-stress-tension-count-${tone}`}>
                {openTensionCount} tension{openTensionCount === 1 ? '' : 's'}
              </span>
            )}
            <span className="ws-stress-health" title={`Health score ${thesis._health}/100`}>
              {thesis._health}
            </span>
          </div>
        </div>
        <div className="ws-stress-premise">
          {thesis.premise_short || thesis.premise || '(unnamed thesis)'}
        </div>

        {/* Pressure gauge: evidence_for vs evidence_against */}
        {totalEv > 0 && (
          <div className="ws-stress-gauge" title={`${evFor} for · ${evAgainst} against`}>
            <div className="ws-stress-gauge-for" style={{ width: `${pctFor}%` }} />
            <div className="ws-stress-gauge-against" style={{ width: `${100 - pctFor}%` }} />
          </div>
        )}

        {topTension && !expanded && (
          <div className={`ws-stress-top-tension ws-stress-top-tension-${SEVERITY_TONES[topTension.severity] || 'steel'}`}>
            <span className="ws-stress-tension-sev">{topTension.severity || 'note'}</span>
            <span className="ws-stress-tension-headline">{topTension.headline}</span>
          </div>
        )}
      </button>

      {expanded && (
        <div className="ws-stress-drawer">
          <div className="ws-stress-premise-full">{thesis.premise}</div>

          {totalEv > 0 && (
            <MetaField label="evidence">
              {evFor} for · {evAgainst} against
            </MetaField>
          )}

          {tensions.length > 0 && (
            <SectionStrip label="tensions" count={tensions.length}>
              <div className="ws-stress-tension-list">
                {tensions.map((t) => (
                  <div
                    key={t.id}
                    className={`ws-stress-tension ws-stress-tension-${SEVERITY_TONES[t.severity] || 'steel'}`}
                  >
                    <div className="ws-stress-tension-head">
                      <span className="ws-stress-tension-sev">{t.severity || 'note'}</span>
                      <span className="ws-stress-tension-headline">{t.headline}</span>
                    </div>
                    {t.body && (
                      <div className="ws-stress-tension-body">{t.body.slice(0, 240)}</div>
                    )}
                    {t.suggested_resolution && (
                      <div className="ws-stress-tension-resolution">
                        → {t.suggested_resolution.slice(0, 200)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </SectionStrip>
          )}

          {tensions.length === 0 && (
            <div className="ws-stress-no-tensions">
              No open tensions. The engine hasn’t detected drift against this
              thesis in the current pass.
            </div>
          )}

          {thesis.state === 'contradicted' && thesis.contradicted_by && (
            <PractitionerLens label="contradicted by">
              {typeof thesis.contradicted_by === 'string'
                ? thesis.contradicted_by
                : JSON.stringify(thesis.contradicted_by)}
            </PractitionerLens>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ───
function headlineFor(counts) {
  if (counts.contradicted > 0) return `${counts.contradicted} contradicted — action required.`;
  if (counts.weakening > 0) return `${counts.weakening} weakening — pay attention.`;
  return 'All active theses holding.';
}

function summaryFor(counts) {
  if (counts.contradicted > 0) {
    return 'Review the contradicted thesis in the drawer. Either update the premise or retire it — running against breaking evidence compounds noise.';
  }
  if (counts.weakening > 0) {
    return 'Trend drifting against these theses. Not broken yet, but worth logging what you’re seeing in the opposite direction.';
  }
  return 'Nothing drifting against your premises right now. Riskiest thesis still surfaces at the top.';
}
